import { useDispatch } from 'react-redux';
import { fetchBrand } from 'app/models/entities/remote';
import { makeCachedLoader } from 'app/containers/state/makeCachedLoader';
import { CancelToken } from 'app/utils/cancelToken';
import { AppDispatch } from 'redux';
import { EntityStringId } from 'app/types/entity';
import { AppState } from 'app/store';
import { getEntityOfTypeOrThrow } from 'app/models/entities/selectors';
import { Brand, Text, BrandReference, TextTranslation } from 'app/constants/schemas';
import { serializeEntity } from 'app/remote';
import { updateFromServer } from 'app/remote/generic/actions';

interface ILoadArgs {
  brandId: EntityStringId | undefined;
  dispatch: AppDispatch;
}

export const ensureBrandReference = (brandId: EntityStringId) => (dispatch: AppDispatch, getState: () => AppState) => {
  const brand = getEntityOfTypeOrThrow(getState(), Brand, brandId);
  const brandName = getEntityOfTypeOrThrow(getState(), Text, brand.name);
  const nameTranslations = brandName.textTranslations.map(
    tId => getEntityOfTypeOrThrow(getState(), TextTranslation, tId),
  );

  // The serializeEntity function requires a recordized view of the entire entity tree
  const brandNameWithTranslations = brandName.merge({ textTranslations: nameTranslations as any });
  /**
   * Simulate an update from the server to include the brand reference view of the brand that was just loaded.
   *
   * This is a relatively gross way to ensure that the brand reference will
   * be defined wherever we've requested the brand
   */
  dispatch(updateFromServer(serializeEntity(new BrandReference({
    id: brand.id,
    // Have to lie about the types because this is building a recordized view but BrandReference expects
    // EntityFields
    name: brandNameWithTranslations as any,
  }))));
};

const { useLoader } = makeCachedLoader(
  async (loadArgs: ILoadArgs, cancelToken: CancelToken) => {
    const { brandId, dispatch } = loadArgs;
    if (brandId === undefined) {
      return;
    }

    const response = await dispatch(
      fetchBrand(brandId, cancelToken),
    );

    dispatch(ensureBrandReference(brandId));

    return response;
  },
);

export const useLoadBrand = (
  brandId: EntityStringId | undefined,
) => {
  const dispatch = useDispatch();

  const result = useLoader({
    args: { brandId, dispatch },
  });

  return result;
};
